import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { api, apiUpload, apiUrl, queryKeys } from '@/shared/api';

import type {
  FeedbackAttachment,
  FeedbackFilter,
  FeedbackPerson,
  FeedbackStatus,
  NewSuggestionInput,
  Suggestion,
  SuggestionPage,
} from './types';

const PAGE_SIZE = 20;

/** The list, a page at a time (server-side filters, sorting and keyset paging). */
export function useSuggestionList(filter: FeedbackFilter, enabled = true) {
  return useInfiniteQuery<
    SuggestionPage,
    Error,
    InfiniteData<SuggestionPage, string | null>,
    readonly unknown[],
    string | null
  >({
    queryKey: queryKeys.feedback.list(filter),
    initialPageParam: null,
    queryFn: ({ signal, pageParam }) =>
      api.get<SuggestionPage>('/v1/feedback/suggestions', {
        signal,
        query: {
          scope: filter.scope,
          q: filter.q.trim() || undefined,
          status: filter.status || undefined,
          category: filter.category || undefined,
          sort: filter.sort,
          assigned: filter.scope === 'all' ? filter.assigned || undefined : undefined,
          attention: filter.scope === 'all' && filter.attention ? true : undefined,
          unread: filter.unread ? true : undefined,
          cursor: pageParam,
          limit: PAGE_SIZE,
        },
      }),
    getNextPageParam: (last) => last.next_cursor,
    enabled,
  });
}

export function useSuggestion(suggestionId: string) {
  return useQuery({
    queryKey: queryKeys.feedback.detail(suggestionId),
    queryFn: ({ signal }) =>
      api.get<Suggestion>(`/v1/feedback/suggestions/${suggestionId}`, { signal }),
  });
}

export function useFeedbackModerators(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.feedback.moderators,
    queryFn: ({ signal }) => api.get<FeedbackPerson[]>('/v1/feedback/moderators', { signal }),
    enabled,
    staleTime: 60_000,
  });
}

function useAfterChange() {
  const client = useQueryClient();
  return (suggestion?: Suggestion) => {
    if (suggestion) client.setQueryData(queryKeys.feedback.detail(suggestion.id), suggestion);
    return client.invalidateQueries({ queryKey: queryKeys.feedback.lists });
  };
}

export function useCreateSuggestion() {
  const after = useAfterChange();
  return useMutation({
    mutationFn: (input: NewSuggestionInput) =>
      api.post<Suggestion>('/v1/feedback/suggestions', input),
    onSuccess: (suggestion) => after(suggestion),
  });
}

export function usePostMessage(suggestionId: string) {
  const after = useAfterChange();
  return useMutation({
    mutationFn: (input: { content: string; internal: boolean; attachment_ids: string[] }) =>
      api.post<Suggestion>(`/v1/feedback/suggestions/${suggestionId}/messages`, input),
    onSuccess: (suggestion) => after(suggestion),
  });
}

export function useChangeStatus(suggestionId: string) {
  const after = useAfterChange();
  return useMutation({
    mutationFn: (input: {
      status: FeedbackStatus;
      note: string | null;
      expected_status: FeedbackStatus;
    }) => api.post<Suggestion>(`/v1/feedback/suggestions/${suggestionId}/status`, input),
    onSuccess: (suggestion) => after(suggestion),
  });
}

export function useAssign(suggestionId: string) {
  const after = useAfterChange();
  return useMutation({
    mutationFn: (assigneeId: string | null) =>
      api.put<Suggestion>(`/v1/feedback/suggestions/${suggestionId}/assignment`, {
        assignee_id: assigneeId,
      }),
    onSuccess: (suggestion) => after(suggestion),
  });
}

export function useSetAttention(suggestionId: string) {
  const after = useAfterChange();
  return useMutation({
    mutationFn: (needsAttention: boolean) =>
      api.put<Suggestion>(`/v1/feedback/suggestions/${suggestionId}/attention`, {
        needs_attention: needsAttention,
      }),
    onSuccess: (suggestion) => after(suggestion),
  });
}

export function useWithdraw(suggestionId: string) {
  const after = useAfterChange();
  return useMutation({
    mutationFn: () => api.post<Suggestion>(`/v1/feedback/suggestions/${suggestionId}/withdraw`),
    onSuccess: (suggestion) => after(suggestion),
  });
}

/** Called once when the conversation is opened (listing or loading never marks it read). */
export function useMarkRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (suggestionId: string) => api.post(`/v1/feedback/suggestions/${suggestionId}/read`),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.feedback.lists }),
  });
}

export function uploadFeedbackAttachment(
  file: Blob,
  filename: string,
  signal?: AbortSignal,
): Promise<FeedbackAttachment> {
  return apiUpload<FeedbackAttachment>('/v1/feedback/attachments', file, {
    filename,
    ...(signal && { signal }),
  });
}

export function discardFeedbackAttachment(id: string): Promise<void> {
  return api.delete(`/v1/feedback/attachments/${id}`);
}

export function feedbackAttachmentUrl(
  id: string,
  kind: 'download' | 'thumb' | 'medium' | 'original',
): string {
  return kind === 'download'
    ? apiUrl(`/v1/feedback/attachments/${id}/download`)
    : apiUrl(`/v1/feedback/attachments/${id}/preview`, { variant: kind });
}
